'use client'
import { styled } from '@mui/material'
import '../src/Theme/UtilityClasses.css'
import '../src/Theme/Fonts.css'
import FooterColumn from '../src/components/UI/Shared/Footer/FooterColumn'
import CompanyAddress from '../src/components/UI/Shared/Footer/CompanyAddress'
import Disclaimer from '../src/components/UI/Shared/CompanyInformation/Disclaimer'
import Header from '../src/components/UI/Shared/Header/Header'
import { layoutMaxWidth } from '../src/Theme/UtilityStyles'
import SocialIcons from '@/components/UI/Shared/Social/SocialIcons'
import { reportCSPViolation } from '../src/utils/csp/reportCSP'
import { useEffect, useRef } from 'react'
import dynamic from 'next/dynamic'
import { MicrosoftClarityProps } from '@/types/Clarity'
import Image from 'next/image'

const MicrosoftClarity = dynamic<MicrosoftClarityProps>(
  () => import('@/components/MicrosoftClarity/MicrosoftClarity'),
  { ssr: false }
)

export const BodyContainer = styled('main')(({ theme }) => ({
  ...layoutMaxWidth,
  margin: '0 auto',
  padding: `${theme.spacing(6)} ${theme.spacing(4)} 0 ${theme.spacing(4)}`
}))

const Footer = styled('footer')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: `${theme.spacing(5)} ${theme.spacing(0)} ${theme.spacing(5)}`,
  '& .footer': {
    '&__inner': {
      ...layoutMaxWidth,
      ul: {
        marginBottom: theme.spacing(4)
      }
    },
    '&__links': {
      display: 'flex'
    },
    '&__social-media': {
      margin: `${theme.spacing(5)} 0`
    }
  },
  address: {
    fontStyle: 'normal',
    fontWeight: 400,
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.6
  }
}))

export default function RootLayoutChildren({
  children,
  zutoUrl,
  nonce,
  isLoggedIn
}: {
  children: React.ReactNode
  zutoUrl: string
  nonce: string
  isLoggedIn: boolean
}) {
  const companyLinks = [
    { text: 'About Zuto', url: `${zutoUrl}/about-zuto/` },
    { text: 'Contact us', url: `${zutoUrl}/contact-us/` },
    { text: 'Careers', url: 'https://careers.zuto.com/' },
    { text: 'Gender pay report', url: `${zutoUrl}/gender-pay-report/` },
    { text: 'B Corp impact report', url: `${zutoUrl}/bcorp-impact-report/` },
    { text: 'Press office', url: `${zutoUrl}/press-office/` },
    { text: 'Modern slavery statement', url: `${zutoUrl}/modern-slavery-statement/` },
    {
      text: 'Customer care and complaints',
      url: `${zutoUrl}/customer-care-and-complaints/`
    }
  ]

  const legalLinks = [
    { text: 'Terms & conditions', url: `${zutoUrl}/terms-and-conditions/` },
    {
      text: 'Clearscore Jan Giveaway T&Cs',
      url: `${zutoUrl}/terms-and-conditions-clearscore/`
    },
    { text: 'Privacy policy', url: `${zutoUrl}/privacy/` },
    { text: 'Cookie policy', url: `${zutoUrl}/cookies/` },
    {
      text: 'Initial disclosure document',
      url: `${zutoUrl}/initial-disclosure-document/`
    },
    { text: 'Sitemap', url: `${zutoUrl}/sitemap/` }
  ]

  const reportedViolations = useRef(new Set<string>())
  const reportedCount = useRef(0)
  const MAX_CSP_REPORTS_PER_SESSION = 40

  useEffect(() => {
    const handleCSPViolation = (e: SecurityPolicyViolationEvent) => {
      const violationKey = `${e.blockedURI}|${e.violatedDirective}`
      if (reportedViolations.current.has(violationKey)) return
      if (reportedCount.current >= MAX_CSP_REPORTS_PER_SESSION) return

      reportedViolations.current.add(violationKey)
      reportedCount.current += 1

      reportCSPViolation({
        'csp-report': {
          documentURI: e.documentURI,
          referrer: e.referrer,
          blockedURI: e.blockedURI,
          violatedDirective: e.violatedDirective,
          originalPolicy: e.originalPolicy
        }
      })
    }
    window.addEventListener('securitypolicyviolation', handleCSPViolation)
    return () => window.removeEventListener('securitypolicyviolation', handleCSPViolation)
  }, [])

  return (
    <>
      <Header zutoUrl={zutoUrl} isLoggedIn={isLoggedIn} />
      <MicrosoftClarity nonce={nonce} />
      {children}
      <Footer>
        <div className="footer__inner">
          <div className="footer__links">
            <FooterColumn title="Company" links={companyLinks}>
              <Image
                src={`https://cdn.zuto.cloud/assets/images/homepage/bcorp-2018-logo.svg`}
                alt="B Corp 2018 Logo"
                width={54}
                height={90}
                loading="eager"
                style={{ marginBottom: '24px' }}
              />
            </FooterColumn>
            <FooterColumn title="Legal" links={legalLinks}>
              <CompanyAddress />
            </FooterColumn>
          </div>
          <div className="footer__social-media">
            <SocialIcons />
          </div>
          <div className="footer__disclaimer">
            <Disclaimer />
          </div>
        </div>
      </Footer>
    </>
  )
}
